/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { combineReducers } from 'redux';
import SeamlessImmutable from 'seamless-immutable';
import { noop, difference, isEqual } from 'underscore';
import { EMPTY_ARRAY, isArrayValuesEqual } from 'DesignData/lib/array';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { tryParseNumber } from 'DesignData/lib/text';
import { isExpired } from 'DesignData/lib/localStorage';
import { getTimestampForAction } from 'DesignData/lib/actions';
import { CODEMIRROR_SESSION_FETCH, CODEMIRROR_SESSION_DELETE, CODEMIRROR_SESSION_EDITOR_UPDATE, CODEMIRROR_SESSION_EDITOR_REMOVE } from 'DesignData/actions/ActionTypes';
/*
 {
  updated: 1505416733427,
  sortedEditors: [sessionId],
  editors: {
    [sessionId]: {
      updated: 1505416733427,
      scrollInfo: {},
      selections: [],
      foldIndices: [],
    },
  },
 }
 */

/*
 * Prevent serialization.  Session is persisted to localStorage.
 */

function preventSerialization(reducer) {
  return (state, action) => {
    state = reducer(state, action);
    return state.toJSON === noop ? state : SeamlessImmutable.set(state, 'toJSON', noop);
  };
}

const editorSession = combineReducers({
  /**
   * updated
   */
  updated(state = null, action) {
    switch (action.type) {
      case CODEMIRROR_SESSION_EDITOR_UPDATE:
        return getTimestampForAction(action);

      default:
        return state;
    }
  },

  /**
   * scrollInfo
   */
  scrollInfo(state = EMPTY_OBJECT, action) {
    switch (action.type) {
      case CODEMIRROR_SESSION_EDITOR_UPDATE:
        return action.session.scrollInfo || state;

      default:
        return state;
    }
  },

  /**
   * selections
   */
  selections(state = EMPTY_ARRAY, action) {
    switch (action.type) {
      case CODEMIRROR_SESSION_EDITOR_UPDATE:
        return action.session.selections || state;

      default:
        return state;
    }
  },

  /**
   * foldIndices
   */
  foldIndices(state = EMPTY_ARRAY, action) {
    switch (action.type) {
      case CODEMIRROR_SESSION_EDITOR_UPDATE:
        return action.session.foldIndices || state;

      default:
        return state;
    }
  },

  /**
   * history
   */
  history: preventSerialization((state = SeamlessImmutable({
    done: EMPTY_ARRAY,
    undone: EMPTY_ARRAY
  }), action) => {
    switch (action.type) {
      case CODEMIRROR_SESSION_EDITOR_UPDATE:
        return action.session.history || state;

      default:
        return state;
    }
  }),

  /**
   * historySize
   */
  historySize: preventSerialization((state = SeamlessImmutable({
    redo: 0,
    undo: 0
  }), action) => {
    switch (action.type) {
      case CODEMIRROR_SESSION_EDITOR_UPDATE:
        return action.session.historySize || state;

      default:
        return state;
    }
  })
});

function editors(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case CODEMIRROR_SESSION_DELETE:
      {
        return EMPTY_OBJECT;
      }

    case CODEMIRROR_SESSION_FETCH:
      {
        if (!action.session) {
          return state;
        }

        if (isExpired(action.session)) {
          return EMPTY_OBJECT;
        }

        const {
          editors: editorsCollection
        } = action.session;

        if (!editorsCollection) {
          return state;
        }

        const sessionIds = Object.keys(editorsCollection);
        let length = sessionIds.length;

        if (!length) {
          return state;
        }

        let editorCount = 0;
        const newState = {}; // Remove expired and call child reducers

        while (length--) {
          const sessionId = sessionIds[length];
          const editorSesh = editorsCollection[sessionId] || EMPTY_OBJECT;
          if (isExpired(editorSesh)) continue;
          ++editorCount;
          const currentSesh = state[sessionId] || EMPTY_OBJECT;

          if (currentSesh.updated && currentSesh.updated >= (editorSesh.updated || 0)) {
            newState[sessionId] = currentSesh;
            continue;
          }

          const childData = Object.assign({}, currentSesh, {}, editorSesh);
          newState[sessionId] = editorSession(childData, action);
        }

        if (!editorCount) {
          return EMPTY_OBJECT;
        }

        if (state !== EMPTY_OBJECT && isEqual(state, newState)) {
          return state;
        }

        return SeamlessImmutable(newState);
      }

    case CODEMIRROR_SESSION_EDITOR_UPDATE:
      return SeamlessImmutable.merge(state, {
        [action.id]: editorSession(state[action.id], action)
      }, {
        deep: true
      });

    case CODEMIRROR_SESSION_EDITOR_REMOVE:
      return SeamlessImmutable.without(state, Array.from(action.ids));

    default:
      return state;
  }
}
/**
 * Sorted by updated timestamp desc.
 * E.g. most recent first.
 */


function sortedEditors(state = EMPTY_ARRAY, action) {
  switch (action.type) {
    case CODEMIRROR_SESSION_DELETE:
      return EMPTY_ARRAY;

    case CODEMIRROR_SESSION_EDITOR_UPDATE:
      {
        const {
          id
        } = action;

        if (!id) {
          return state;
        } // Session is most recent


        if (state[0] === id) {
          return state;
        } // Empty state


        if (state === EMPTY_ARRAY || !state.length) {
          return state.concat(id);
        }

        const index = state.indexOf(id); // Session not in list

        if (!~index) {
          return SeamlessImmutable([id, ...state]);
        } // Session already in list, move to top


        return SeamlessImmutable([id, ...state.slice(0, index), ...state.slice(index + 1)]);
      }

    case CODEMIRROR_SESSION_EDITOR_REMOVE:
      {
        if (state === EMPTY_ARRAY || !state.length) {
          return EMPTY_ARRAY;
        }

        const {
          ids
        } = action;

        if (!(ids && ids.length)) {
          return state;
        }

        const newList = difference(state, ids);

        if (isArrayValuesEqual(state, newList)) {
          return state;
        }

        return newList.length ? SeamlessImmutable(newList) : EMPTY_ARRAY;
      }

    case CODEMIRROR_SESSION_FETCH:
      {
        if (!action.session) return state;

        if (isExpired(action.session)) {
          return EMPTY_ARRAY;
        }

        const {
          sortedEditors: __sorted
        } = action.session; // Fetched matches current state

        if (__sorted && isArrayValuesEqual(state, __sorted)) {
          return state;
        }

        const {
          editors: __editors
        } = action.session;
        if (!__editors) return EMPTY_ARRAY;
        const isSorted = !!(__sorted && __sorted.length);
        const newList = [];
        const oldList = isSorted ? __sorted : Object.keys(__editors); // Remove expired

        oldList.forEach(id => {
          id = tryParseNumber(id);
          const sesh = __editors[id];

          if (!sesh || isExpired(sesh)) {
            return;
          }

          if (isSorted) {
            newList.push(id);
            return;
          } // If no existing sortedEditors, create from editors collection.


          const idx = newList.findIndex(__id => {
            return sesh.updated >= __editors[__id].updated;
          });

          if (!~idx) {
            newList.push(id);
          } else {
            newList.splice(idx, 0, id);
          }
        });

        if (isArrayValuesEqual(state, newList)) {
          return state;
        }

        return newList.length ? SeamlessImmutable(newList) : EMPTY_ARRAY;
      }

    default:
      return state;
  }
}
/**
 * Timestamp for removing old session data.
 */


function updated(state = null, action) {
  switch (action.type) {
    case CODEMIRROR_SESSION_EDITOR_UPDATE:
      return getTimestampForAction(action);

    case CODEMIRROR_SESSION_DELETE:
      return null;

    case CODEMIRROR_SESSION_FETCH:
      {
        if (isExpired(action.session)) {
          return null;
        }

        return action.session && action.session.updated || state;
      }

    default:
      return state;
  }
}

export default combineReducers({
  editors,
  sortedEditors,
  updated
});